import React, { useState, useEffect } from 'react'
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  message,
  Tag,
  Tooltip,
  Space,
  DatePicker
} from 'antd'
import { PlusOutlined, EditOutlined, PrinterOutlined, FileTextOutlined } from '@ant-design/icons'
import moment from 'moment'
import { useSelector } from 'react-redux'
import { BillingEnums } from '../../utils/enums'
import {
  getCreditNotes,
  createCreditNote,
  updateCreditNote,
  getInvoiceDetails
} from '../../backend-integration/api/creditNotes'
import CreditNoteForm from './CreditNoteForm'

const CreditNoteManager = () => {
  const authState = useSelector((state) => state.auth)
  const user = authState?.agiliteUser
  const [loading, setLoading] = useState(false)
  const [creditNotes, setCreditNotes] = useState([])
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [editingCreditNote, setEditingCreditNote] = useState(null)
  const [form] = Form.useForm()
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  })

  useEffect(() => {
    fetchCreditNotes()
  }, [pagination.current, pagination.pageSize])

  const fetchCreditNotes = async () => {
    setLoading(true)
    try {
      const { data, total } = await getCreditNotes({}, pagination.current, pagination.pageSize)
      setCreditNotes(data)
      setPagination((prev) => ({ ...prev, total }))
    } catch (error) {
      message.error('Failed to fetch credit notes')
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  const handleCreate = () => {
    setEditingCreditNote(null)
    form.resetFields()
    setIsModalVisible(true)
  }

  const handleEdit = (record) => {
    setEditingCreditNote(record)
    form.setFieldsValue({
      ...record,
      date: moment(record.date)
    })
    setIsModalVisible(true)
  }

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields()
      setLoading(true)

      const creditNoteData = {
        ...values,
        date: values.date.toDate(),
        status: values.status || BillingEnums.creditNoteStatus.DRAFT
      }

      if (editingCreditNote) {
        await updateCreditNote(editingCreditNote._id, creditNoteData)
        message.success('Credit note updated successfully')
      } else {
        await createCreditNote(creditNoteData)
        message.success('Credit note created successfully')
      }

      setIsModalVisible(false)
      fetchCreditNotes()
    } catch (error) {
      console.error('Error saving credit note:', error)
      message.error('Failed to save credit note')
    } finally {
      setLoading(false)
    }
  }

  const getStatusColor = (status) => {
    const colors = {
      [BillingEnums.creditNoteStatus.DRAFT]: 'default',
      [BillingEnums.creditNoteStatus.APPROVED]: 'success',
      [BillingEnums.creditNoteStatus.APPLIED]: 'processing',
      [BillingEnums.creditNoteStatus.CANCELLED]: 'error'
    }
    return colors[status] || 'default'
  }

  const columns = [
    {
      title: 'Credit Note #',
      dataIndex: 'creditNoteNo',
      key: 'creditNoteNo',
      render: (text) => <a>{text}</a>
    },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      render: (date) => moment(date).format('YYYY-MM-DD')
    },
    {
      title: 'Invoice #',
      dataIndex: 'invoiceNo',
      key: 'invoiceNo'
    },
    {
      title: 'Patient',
      dataIndex: 'patientName',
      key: 'patientName'
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount) => `R ${amount.toFixed(2)}`,
      align: 'right'
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => <Tag color={getStatusColor(status)}>{status}</Tag>
    },
    {
      title: 'Reason',
      dataIndex: 'reason',
      key: 'reason',
      ellipsis: {
        showTitle: false
      },
      render: (reason) => (
        <Tooltip placement='topLeft' title={reason}>
          {reason}
        </Tooltip>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          {record.status === BillingEnums.creditNoteStatus.DRAFT && (
            <Button icon={<EditOutlined />} size='small' onClick={() => handleEdit(record)}>
              Edit
            </Button>
          )}
          <Button
            icon={<PrinterOutlined />}
            size='small'
            onClick={() => window.open(`/billing/credit-note/print/${record._id}`, '_blank')}
          >
            Print
          </Button>
        </Space>
      )
    }
  ]

  return (
    <Card
      title='Credit Notes'
      extra={
        <Button type='primary' icon={<PlusOutlined />} onClick={handleCreate}>
          Create Credit Note
        </Button>
      }
    >
      <Table
        columns={columns}
        dataSource={creditNotes}
        rowKey='_id'
        loading={loading}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} credit notes`,
          onChange: (page, pageSize) => {
            setPagination((prev) => ({
              ...prev,
              current: page,
              pageSize
            }))
          }
        }}
      />

      <Modal
        title={editingCreditNote ? 'Edit Credit Note' : 'Create Credit Note'}
        visible={isModalVisible}
        onOk={handleModalOk}
        onCancel={() => setIsModalVisible(false)}
        width={800}
        confirmLoading={loading}
      >
        <CreditNoteForm form={form} editingCreditNote={editingCreditNote} />
      </Modal>
    </Card>
  )
}

export default CreditNoteManager
